/**
 * Integer-cents money helpers for the frontend VAT UI.
 *
 * Mirrors backoffice-lambda-backend-V2's `src/functions/vat/money.ts` so both sides agree on the
 * exact same cents for the same input — no floating-point arithmetic ever touches a money or VAT
 * value. `toCents` converts a decimal value (string or number) to exact integer cents using
 * `toFixed(2)` (exact decimal rounding), never a bare `Math.round(value * 100)`, which can drift
 * on values whose binary float representation isn't exact (e.g. 1.005 * 100 ===
 * 100.49999999999999 in raw IEEE-754). `grossCents` adds VAT, computed on integer cents with only
 * integer `*`/`+`/`floor` and explicit half-up rounding, to the net.
 */

/**
 * Converts a decimal value to integer cents, exactly.
 */
export function toCents(value: string | number): number {
  const n = Number(value)
  if (!Number.isFinite(n)) {
    throw new Error(`toCents: not a finite number: ${String(value)}`)
  }

  const fixed = n.toFixed(2) // e.g. "29.99", "-5.00"
  const negative = fixed.startsWith('-')
  const unsigned = negative ? fixed.slice(1) : fixed
  const [whole, frac] = unsigned.split('.')
  const cents = Number(whole) * 100 + Number(frac)

  return negative ? -cents : cents
}

/**
 * Converts integer cents back to a decimal value for display, exactly — the inverse of
 * `toCents`. Built from the integer whole/fraction parts rather than a bare `cents / 100` so the
 * result never carries float residue from the division.
 */
export function fromCents(cents: number): number {
  if (!Number.isFinite(cents)) {
    throw new Error(`fromCents: not a finite number: ${String(cents)}`)
  }

  const negative = cents < 0
  const abs = Math.round(Math.abs(cents))
  const whole = Math.floor(abs / 100)
  const frac = abs % 100
  const value = whole + frac / 100

  return negative ? -value : value
}

/**
 * Net cents plus VAT (percent × 100, e.g. 19.00% → 1900) at `rateHundredths`, integer half-up.
 *
 * `Math.floor((netCents * rateHundredths + 5000) / 10000)` is the VAT amount — all operands are
 * integers (cents and hundredths-of-a-percent), so this is exact integer arithmetic. The `+ 5000`
 * before the integer-divide-by-10000 (via floor) implements half-up rounding to the nearest cent.
 */
export function grossCents(netCents: number, rateHundredths: number): number {
  const vatCents = Math.floor((netCents * rateHundredths + 5000) / 10000)
  return netCents + vatCents
}
